import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../lib/api';
import { Search, Users, Star, ClipboardList } from 'lucide-react';
import StudentFilesManager from '../components/StudentFilesManager';
import SchuelerStammblattModal from '../components/SchuelerStammblattModal';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { loadStudentPhotos, createPhotoDataUrl, hasPhoto } from '../lib/photoUtils';

interface Student {
  ID: number;
  Name: string;
  Vorname: string;
  Klasse: string;
  sophomorixUnid: string;
  isFavorite?: boolean;
  hasPrivateFile?: boolean;
  hasAnyFile?: boolean;
  isClassTeacher?: boolean;
  pendingApprovalCount?: number;
}

export default function StudentFiles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showStammblatt, setShowStammblatt] = useState(false);
  const [studentPhotos, setStudentPhotos] = useState<Record<string, string | null>>({});
  
  // Fetch students with search support (includes search in file categories/descriptions)
  const { data: studentsData, isLoading } = useQuery(
    ['students', searchTerm],
    async () => {
      const params = searchTerm ? { search: searchTerm } : {};
      console.log('[StudentFiles] Fetching students with params:', params);
      const response = await api.get('/students', { params });
      console.log('[StudentFiles] Received students:', response.data?.data?.length || 0);
      return response.data;
    },
    {
      onError: (error: any) => {
        console.error('Error fetching students:', error);
        toast.error('Fehler beim Laden der Schüler');
      }
    }
  );

  const students: Student[] = studentsData?.data || [];

  // Fotos in Batches laden (vermeidet 429 Too Many Requests bei vielen Schülern)
  useEffect(() => {
    if (students.length === 0) return;
    const sophomorixUnids = students
      .map((s) => s.sophomorixUnid)
      .filter((id): id is string => Boolean(id));
    const uncachedIds = sophomorixUnids.filter(
      (id) => !Object.prototype.hasOwnProperty.call(studentPhotos, id)
    );
    if (uncachedIds.length === 0) return;

    const batchSize = 20;
    const batches: string[][] = [];
    for (let i = 0; i < uncachedIds.length; i += batchSize) {
      batches.push(uncachedIds.slice(i, i + batchSize));
    }

    let cancelled = false;
    const loadBatches = async () => {
      for (let i = 0; i < batches.length && !cancelled; i++) {
        try {
          const photos = await loadStudentPhotos(batches[i]);
          if (!cancelled) {
            setStudentPhotos((prev) => ({ ...prev, ...photos }));
          }
        } catch (err) {
          console.warn('[StudentFiles] Fehler beim Laden eines Foto-Batches:', err);
        }
        if (i < batches.length - 1) {
          await new Promise((r) => setTimeout(r, 100));
        }
      }
    };
    loadBatches();
    return () => {
      cancelled = true;
    };
  }, [students]);

  // Foto für ausgewählten Schüler nachladen (z.B. bei Direktaufruf mit ?student=)
  useEffect(() => {
    const unid = selectedStudent?.sophomorixUnid;
    if (!unid || Object.prototype.hasOwnProperty.call(studentPhotos, unid)) return;
    loadStudentPhotos([unid]).then((photos) => {
      setStudentPhotos((prev) => ({ ...prev, ...photos }));
    }).catch(() => {});
  }, [selectedStudent?.sophomorixUnid]);

  // Check if student query parameter is set
  useEffect(() => {
    const studentUnid = searchParams.get('student');
    if (studentUnid && !selectedStudent) {
      // Try to find student in already loaded data first
      const existingStudent = students.find((s: Student) => s.sophomorixUnid === studentUnid);
      if (existingStudent) {
        setSelectedStudent(existingStudent);
        // Scroll to official files if hash is present
        setTimeout(() => {
          if (window.location.hash === '#official-files') {
            const element = document.getElementById('official-files');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }, 500);
        // Remove query parameter from URL but keep hash
        const hash = window.location.hash;
        setSearchParams({}, { replace: true });
        if (hash) {
          window.location.hash = hash;
        }
        return;
      }
      
      // If not found, fetch student data directly by UNID
      api.get('/students', { params: { search: studentUnid } })
        .then(response => {
          const fetchedStudents = response.data?.data || [];
          const student = fetchedStudents.find((s: Student) => s.sophomorixUnid === studentUnid);
          if (student) {
            setSelectedStudent(student);
            // Scroll to official files if hash is present
            setTimeout(() => {
              if (window.location.hash === '#official-files') {
                const element = document.getElementById('official-files');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }
            }, 500);
            // Remove query parameter from URL but keep hash
            const hash = window.location.hash;
            setSearchParams({}, { replace: true });
            if (hash) {
              window.location.hash = hash;
            }
          } else {
            toast.error('Schüler nicht gefunden');
          }
        })
        .catch(error => {
          console.error('Error fetching student:', error);
          toast.error('Fehler beim Laden des Schülers');
        });
    }
  }, [searchParams, selectedStudent, setSearchParams, students]);
  const filteredStudents = students; // Already filtered by backend (favorites first)
  const queryClient = useQueryClient();

  // Farbschema: 1) Klassenlehrer (+ Genehmigungen), 2) Private Akte, 3) Mit Akte, 4) Ohne Akte
  type CardCategory = 'klassenlehrer-pending' | 'klassenlehrer' | 'private' | 'with-file' | 'no-file';
  const getCardCategory = (s: Student): CardCategory => {
    if (s.isClassTeacher && (s.pendingApprovalCount || 0) > 0) return 'klassenlehrer-pending';
    if (s.isClassTeacher) return 'klassenlehrer';
    if (s.hasPrivateFile) return 'private';
    if (s.hasAnyFile) return 'with-file';
    return 'no-file';
  };
  const getCardStyles = (cat: CardCategory) => {
    switch (cat) {
      case 'klassenlehrer-pending':
        return {
          card: 'bg-amber-50 border-amber-300 hover:border-amber-400 dark:bg-amber-950/40 dark:border-amber-700 dark:hover:border-amber-600',
          badge: 'bg-amber-500 text-white',
          label: 'Klassenlehrer • Genehmigungen offen',
        };
      case 'klassenlehrer':
        return {
          card: 'bg-blue-50 border-blue-200 hover:border-blue-300 dark:bg-blue-950/40 dark:border-blue-800 dark:hover:border-blue-700',
          badge: null,
          label: 'Meine Klasse',
        };
      case 'private':
        return {
          card: 'bg-violet-50 border-violet-200 hover:border-violet-300 dark:bg-violet-950/40 dark:border-violet-800 dark:hover:border-violet-700',
          badge: null,
          label: 'Private Akte',
        };
      case 'with-file':
        return {
          card: 'bg-white border-emerald-200 hover:border-emerald-300 dark:bg-gray-900 dark:border-emerald-800 dark:hover:border-emerald-700',
          badge: null,
          label: 'Mit Akte',
        };
      default:
        return {
          card: 'bg-gray-50 border-gray-200 opacity-80 hover:opacity-100 hover:border-gray-300 dark:bg-gray-900/60 dark:border-gray-700 dark:hover:border-gray-600 dark:opacity-90 dark:hover:opacity-100',
          badge: null,
          label: 'Ohne digitale Akte',
        };
    }
  };

  // Toggle favorite mutation
  const toggleFavoriteMutation = useMutation(
    async ({ studentUnid, isFavorite }: { studentUnid: string; isFavorite: boolean }) => {
      if (isFavorite) {
        await api.delete(`/students/${studentUnid}/favorite`);
      } else {
        await api.post(`/students/${studentUnid}/favorite`);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['students', searchTerm]);
        toast.success('Favorit aktualisiert');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Fehler beim Aktualisieren des Favoriten');
      },
    }
  );

  // Scroll to official files or access-requests section when student is selected and hash is present
  useEffect(() => {
    if (selectedStudent && window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const timer = setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [selectedStudent]);

  if (selectedStudent) {
    return (
      <div className="flex h-screen flex-col bg-gray-50 dark:bg-gray-950">
        <div className="card flex items-center justify-between rounded-none border-x-0 border-t-0 px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSelectedStudent(null)}
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
            >
              ← Zurück zur Übersicht
            </button>
            <div className="flex items-center gap-4">
              <div className="relative flex h-12 w-12 flex-shrink-0 cursor-default items-center justify-center overflow-visible rounded-full bg-gray-200 dark:bg-gray-700 group">
                <div className="absolute inset-0 flex origin-center items-center justify-center overflow-hidden rounded-full border-2 border-gray-200 shadow-lg transition-transform duration-200 ease-out group-hover:z-50 group-hover:scale-[2.5] group-hover:shadow-xl dark:border-gray-600">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Users className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                  </div>
                  {hasPhoto(studentPhotos[selectedStudent.sophomorixUnid]) && (
                    <img
                      src={createPhotoDataUrl(studentPhotos[selectedStudent.sophomorixUnid]!)}
                      alt={`${selectedStudent.Vorname} ${selectedStudent.Name}`}
                      className="relative z-10 h-full w-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Schülerakte: {selectedStudent.Vorname} {selectedStudent.Name}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Klasse: {selectedStudent.Klasse}</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowStammblatt(true)}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
            title="Schülerstammblatt anzeigen"
          >
            <ClipboardList className="h-5 w-5" />
            <span className="hidden sm:inline">Stammblatt</span>
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          <StudentFilesManager
            studentUnid={selectedStudent.sophomorixUnid}
            studentName={`${selectedStudent.Vorname} ${selectedStudent.Name}`}
            studentClass={selectedStudent.Klasse}
          />
        </div>
        <SchuelerStammblattModal
          isOpen={showStammblatt}
          onClose={() => setShowStammblatt(false)}
          studentUnid={selectedStudent.sophomorixUnid}
        />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-gray-50 dark:bg-gray-950">
      <div className="card rounded-none border-x-0 border-t-0 px-6 py-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Schülerakten</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Verwalten Sie die Akten der Schüler</p>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl px-6 py-6">
          {/* Search bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400 dark:text-gray-500" />
              <input
                type="search"
                placeholder="Schüler suchen (Name, Vorname, Klasse, Schlagwörter in Akten)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          {/* Legende */}
          <div className="mb-6 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full bg-amber-400 dark:bg-amber-500" />
              <span>Klassenlehrer (Genehmigungen offen)</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full bg-blue-300 dark:bg-blue-500" />
              <span>Meine Klasse</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full bg-violet-300 dark:bg-violet-500" />
              <span>Private Akte</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full bg-emerald-300 dark:bg-emerald-500" />
              <span>Mit Akte</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full bg-gray-300 dark:bg-gray-600" />
              <span>Ohne digitale Akte</span>
            </span>
          </div>

          {/* Students list */}
          {isLoading ? (
            <div className="py-12 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600 dark:border-blue-400"></div>
              <p className="mt-4 text-gray-500 dark:text-gray-400">Lade Schüler...</p>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="py-12 text-center">
              <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <p className="mt-4 text-gray-500 dark:text-gray-400">
                {searchTerm ? 'Keine Schüler gefunden' : 'Keine Schüler vorhanden'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredStudents.map((student, index) => {
                const uniqueKey = student.sophomorixUnid 
                  ? `student-${student.sophomorixUnid}-${index}`
                  : `student-${index}-${student.ID || 'no-id'}-${student.Name || 'unknown'}-${student.Vorname || 'unknown'}-${student.Klasse || 'unknown'}`;
                const cat = getCardCategory(student);
                const styles = getCardStyles(cat);
                const pendingCount = student.pendingApprovalCount || 0;
                return (
                <div
                  key={uniqueKey}
                  className={`relative rounded-lg border-2 p-4 shadow-sm transition-all dark:shadow-black/20 ${styles.card}`}
                  title={styles.label}
                >
                  <button
                    onClick={() => setSelectedStudent(student)}
                    className="w-full text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-gray-200 bg-gray-200 dark:border-gray-600 dark:bg-gray-700">
                        <div className="absolute inset-0 z-0 flex items-center justify-center">
                          <Users className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                        </div>
                        {hasPhoto(studentPhotos[student.sophomorixUnid]) && (
                          <img
                            src={createPhotoDataUrl(studentPhotos[student.sophomorixUnid]!)}
                            alt={`${student.Vorname} ${student.Name}`}
                            className="relative z-10 h-full w-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate font-semibold text-gray-900 dark:text-gray-100">
                          {student.Vorname} {student.Name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Klasse: {student.Klasse}
                          {cat === 'klassenlehrer' || cat === 'klassenlehrer-pending' ? (
                            <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">• {styles.label}</span>
                          ) : null}
                        </p>
                      </div>
                      {pendingCount > 0 && (
                        <div className="flex-shrink-0 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-medium text-white dark:bg-amber-600" title={`${pendingCount} Akteneinträge zur Genehmigung`}>
                          {pendingCount}
                        </div>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavoriteMutation.mutate({
                        studentUnid: student.sophomorixUnid,
                        isFavorite: student.isFavorite || false
                      });
                    }}
                    className="absolute right-3 top-3 rounded p-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                    title={student.isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
                  >
                    <Star
                      className={`h-5 w-5 ${
                        student.isFavorite
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-400 hover:text-yellow-400 dark:text-gray-500'
                      } transition-colors`}
                    />
                  </button>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
